import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Activity, Cpu, FolderGit2, Globe, HeartPulse, LayoutGrid, Network, Package, Pause, Play, Rocket, RotateCcw, ScrollText, Search, Settings as SettingsIcon } from "lucide-react";

import { api } from "../lib/api.js";
import { useToast } from "./ui.jsx";

/**
 * Go anywhere, or deploy anything, without reaching for the mouse.
 *
 * Two kinds of entry: places and actions. Actions are the ones that earn it —
 * deploying a project is otherwise two navigations and a click, and it is the
 * thing done most often.
 *
 * Matching is subsequence, not substring, so "opsd" finds "scaling-ops · Deploy".
 * Entries that change something are marked, and a destructive one asks first.
 */

const PAGES = [
    { id: "page:/", label: "Overview", to: "/", Icon: LayoutGrid },
    { id: "page:/projects", label: "Projects", to: "/projects", Icon: Package },
    { id: "page:/deployments", label: "Deployments", to: "/deployments", Icon: Activity },
    { id: "page:/workspaces", label: "Workspaces", to: "/workspaces", Icon: FolderGit2 },
    { id: "page:/status", label: "Status", to: "/status", Icon: HeartPulse },
    { id: "page:/processes", label: "Processes", to: "/processes", Icon: Cpu },
    { id: "page:/ports", label: "Ports", to: "/ports", Icon: Network },
    { id: "page:/domains", label: "Domains", to: "/domains", Icon: Globe },
    { id: "page:/audit", label: "Audit", to: "/audit", Icon: ScrollText },
    { id: "page:/settings", label: "Settings", to: "/settings", Icon: SettingsIcon }
];

/** Every character of the query in order, so short abbreviations work. */
function score(text, query) {
    if (!query) return 0;
    const haystack = text.toLowerCase();
    const needle = query.toLowerCase();

    if (haystack.includes(needle)) return haystack.startsWith(needle) ? 1000 : 500;

    let index = 0;
    let gaps = 0;
    for (const character of needle) {
        const found = haystack.indexOf(character, index);
        if (found === -1) return -1;
        gaps += found - index;
        index = found + 1;
    }
    return 100 - gaps;
}

export function CommandPalette({ open, onClose }) {
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    const [projects, setProjects] = useState([]);
    const [confirming, setConfirming] = useState(null);
    const input = useRef(null);
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setCursor(0);
        setConfirming(null);
        api.projects.list().then(setProjects).catch(() => {});
        // The autofocus has to wait for the element to exist.
        requestAnimationFrame(() => input.current?.focus());
    }, [open]);

    const commands = useMemo(() => {
        const items = PAGES.map((page) => ({ ...page, group: "Go to", run: () => navigate(page.to) }));

        for (const project of projects) {
            items.push({
                id: `project:${project.slug}`,
                label: project.slug,
                hint: `${project.gitlab.path} · ${project.branch}`,
                group: "Projects",
                Icon: Package,
                run: () => navigate(`/projects/${project.slug}`)
            });

            items.push({
                id: `deploy:${project.slug}`,
                label: `${project.slug} · Deploy`,
                group: "Actions",
                Icon: Rocket,
                changes: true,
                run: async () => {
                    const result = await api.projects.deploy(project.slug);
                    toast(`${project.slug} #${result.number} started`);
                    navigate(`/projects/${project.slug}`);
                }
            });

            if (project.strategy === "releases" && project.previousRelease) {
                items.push({
                    id: `rollback:${project.slug}`,
                    label: `${project.slug} · Roll back`,
                    hint: project.previousRelease.split("/").at(-1),
                    group: "Actions",
                    Icon: RotateCcw,
                    changes: true,
                    confirm: `Roll ${project.slug} back to its previous release?`,
                    run: async () => {
                        await api.projects.rollback(project.slug);
                        toast(`${project.slug} rolling back`);
                        navigate(`/projects/${project.slug}`);
                    }
                });
            }

            items.push({
                id: `pause:${project.slug}`,
                label: `${project.slug} · ${project.paused ? "Resume" : "Pause"}`,
                group: "Actions",
                Icon: project.paused ? Play : Pause,
                changes: true,
                run: async () => {
                    await (project.paused ? api.projects.resume(project.slug) : api.projects.pause(project.slug));
                    toast(`${project.slug} ${project.paused ? "resumed" : "paused"}`);
                }
            });
        }

        return items;
    }, [projects, navigate, toast]);

    const results = useMemo(() => {
        if (!query.trim()) return commands.slice(0, 12);
        return commands
            .map((command) => ({ command, rank: score(`${command.label} ${command.hint ?? ""}`, query.trim()) }))
            .filter((row) => row.rank >= 0)
            .sort((a, b) => b.rank - a.rank)
            .slice(0, 12)
            .map((row) => row.command);
    }, [commands, query]);

    useEffect(() => {
        setCursor(0);
    }, [query]);

    if (!open) return null;

    const choose = async (command) => {
        if (command.confirm && confirming !== command.id) {
            setConfirming(command.id);
            return;
        }
        onClose();
        try {
            await command.run();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const onKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setCursor((value) => (value + 1) % Math.max(results.length, 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setCursor((value) => (value - 1 + results.length) % Math.max(results.length, 1));
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (results[cursor]) choose(results[cursor]);
        } else if (event.key === "Escape") {
            event.preventDefault();
            if (confirming) setConfirming(null);
            else onClose();
        }
    };

    let lastGroup = null;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[10vh]" onPointerDown={onClose}>
            <div className="card w-full max-w-lg overflow-hidden shadow-xl" onPointerDown={(event) => event.stopPropagation()}>
                <div className="flex items-center gap-2 border-b border-gray-200 px-3 dark:border-gray-800">
                    <Search size={16} className="muted shrink-0" aria-hidden />
                    <input
                        ref={input}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Go to a page, or deploy a project"
                        aria-label="Command palette"
                        className="w-full bg-transparent py-3 text-base outline-none placeholder:text-gray-400 sm:text-sm dark:placeholder:text-gray-600"
                    />
                </div>

                {results.length === 0 ? (
                    <p className="muted px-4 py-6 text-center text-sm">Nothing matches.</p>
                ) : (
                    <ul className="max-h-80 overflow-y-auto py-1">
                        {results.map((command, index) => {
                            const heading = command.group !== lastGroup ? command.group : null;
                            lastGroup = command.group;
                            const asking = confirming === command.id;

                            return (
                                <li key={command.id}>
                                    {heading ? <p className="nav-group">{heading}</p> : null}
                                    <button
                                        onMouseEnter={() => setCursor(index)}
                                        onClick={() => choose(command)}
                                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm ${
                                            index === cursor ? "bg-gray-100 dark:bg-gray-800" : ""
                                        }`}
                                    >
                                        <command.Icon size={15} className="muted shrink-0" aria-hidden />
                                        <span className="min-w-0 flex-1 truncate">
                                            {asking ? command.confirm : command.label}
                                            {command.hint && !asking ? <span className="muted ml-2 text-xs">{command.hint}</span> : null}
                                        </span>
                                        {asking ? (
                                            <span className="badge-warn shrink-0">Enter to confirm</span>
                                        ) : command.changes ? (
                                            <span className="muted shrink-0 text-xs">runs</span>
                                        ) : null}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <p className="muted flex flex-wrap items-center gap-x-3 border-t border-gray-200 px-3 py-2 text-xs dark:border-gray-800">
                    <span><Key>↑</Key><Key>↓</Key> move</span>
                    <span><Key>↵</Key> run</span>
                    <span><Key>esc</Key> close</span>
                    <span className="ml-auto hidden sm:inline"><Key>/</Key> search this page</span>
                </p>
            </div>
        </div>
    );
}

function Key({ children }) {
    return (
        <kbd className="mr-1 rounded border border-gray-300 px-1 font-sans text-[10px] dark:border-gray-700">{children}</kbd>
    );
}
